import { CityWithStreetsDTO, Street } from "@lg-cfa/services";
import { useEffect, useState } from "react";

import { useDebouncedEffect } from "../useDebouncedEffect";
import { useFlowNavigation } from "../useFlowNavigation";

import { PageNames } from "@enums";

export function useStreetSearch(
    typeAheadStreet: string,
    driverCity?: CityWithStreetsDTO,
    driverStreet?: Street,
) {
    const isSSR = typeof window === "undefined";
    const [streetResults, setStreetResults] = useState<
        Array<Street> | undefined
    >([]);
    const [streetsLoading, setStreetsLoading] = useState<boolean>(false);
    const { navigateTo } = useFlowNavigation();

    const getStreetsFromData = (postalCode: string, searchString: string) => {
        // We are not using the AddressApiClient here because it is endoflife
        // and this endpoint is not included in the API client
        setStreetsLoading(true);
        fetch(
            `/api/address/postalcodes/${postalCode}/streets?streetName=${searchString}`,
        )
            .then((response) => response.json())
            .then((response: CityWithStreetsDTO) => {
                if (response) {
                    if (Array.isArray(response)) {
                        setStreetResults(
                            response?.reduce<Street[]>((acc, city) => {
                                if (city.streets) {
                                    return acc.concat(city.streets);
                                }
                                return acc;
                            }, []),
                        );
                    } else {
                        setStreetResults(response.streets);
                    }
                }
            })
            .catch((error) => {
                setStreetResults(undefined);
                console.error(error);
                navigateTo(PageNames.TechnicalDifficulties);
            });
    };

    useEffect(() => {
        if (
            (streetResults && streetResults.length > 0) ||
            ((!streetResults || streetResults.length === 0) &&
                typeAheadStreet === "")
        ) {
            setStreetsLoading(false);
        }
    }, [streetResults, typeAheadStreet]);

    useDebouncedEffect(
        () => {
            let isAlreadyFetched = false;
            if (
                !isSSR &&
                typeAheadStreet.length > 2 &&
                driverCity?.postalCode
            ) {
                getStreetsFromData(driverCity.postalCode, typeAheadStreet);
                isAlreadyFetched = true;
            } else if (
                !isSSR &&
                driverCity?.postalCode &&
                driverStreet?.name &&
                driverStreet.name.length > 2 &&
                typeAheadStreet.length === 0 &&
                streetResults?.length === 0
            ) {
                getStreetsFromData(driverCity.postalCode, driverStreet.name);
                isAlreadyFetched = true;
            }

            if (
                !isSSR &&
                typeAheadStreet === "" &&
                !isAlreadyFetched &&
                !driverStreet?.id
            ) {
                setStreetResults(undefined);
            }
        },
        300,
        [
            isSSR,
            typeAheadStreet,
            driverCity,
            driverStreet,
            streetResults?.length,
        ],
    );

    return { streetResults, streetsLoading };
}
